import React, { useEffect, useState } from "react";
import type { AssertionOptions, AuthenticationResult } from "../auth/webauthn";
import { startAuthentication, isWebAuthnSupported } from "../auth/webauthn";

interface PasskeyChallengeProps {
  assertionOptions: AssertionOptions;
  onSuccess: (result: AuthenticationResult) => void;
  onError: (error: string) => void;
  onCancel: () => void;
}

/**
 * Displays the WebAuthn challenge UI.
 *
 * When this component mounts, it automatically calls navigator.credentials.get()
 * which triggers the browser's native passkey UI:
 * - On laptop with biometrics: Touch ID / Windows Hello prompt
 * - On laptop without: QR code to scan with phone
 * - On phone: Face ID / fingerprint prompt
 */
export const PasskeyChallenge: React.FC<PasskeyChallengeProps> = ({
  assertionOptions,
  onSuccess,
  onError,
  onCancel,
}) => {
  const [status, setStatus] = useState<"waiting" | "error">("waiting");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!isWebAuthnSupported()) {
      setStatus("error");
      setErrorMessage("WebAuthn is not supported in this browser.");
      onError("WebAuthn not supported");
      return;
    }

    let cancelled = false;

    const doAuth = async () => {
      try {
        const result = await startAuthentication(assertionOptions);
        if (!cancelled) {
          onSuccess(result);
        }
      } catch (err: any) {
        if (!cancelled) {
          const msg = err.message || "Authentication failed";
          setStatus("error");
          setErrorMessage(msg);
          onError(msg);
        }
      }
    };

    doAuth();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="passkey-challenge-container">
      <div className="passkey-challenge-card">
        {status === "waiting" && (
          <>
            <div className="passkey-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" style={{ stroke: "var(--color-brand)" }} strokeWidth="1.5">
                <path d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
              </svg>
            </div>
            <h2>Verify your identity</h2>
            <p>
              Use your passkey to sign in. Your browser will prompt you to
              use Face ID, fingerprint, or scan a QR code with your phone.
            </p>
            <div className="passkey-spinner" />
            <button className="passkey-cancel" onClick={onCancel}>
              Cancel
            </button>
          </>
        )}

        {status === "error" && (
          <>
            <div className="passkey-icon passkey-icon-error">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" style={{ stroke: "var(--color-danger)" }} strokeWidth="1.5">
                <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            </div>
            <h2>Authentication failed</h2>
            <p className="passkey-error-msg">{errorMessage}</p>
            <button className="passkey-retry" onClick={() => window.location.reload()}>
              Try again
            </button>
          </>
        )}
      </div>

      <style>{`
        .passkey-challenge-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(145deg, var(--color-surface) 0%, var(--color-bg) 50%, var(--color-accent-ghost) 100%);
          padding: 20px;
        }
        .passkey-challenge-card {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
          padding: 48px 40px;
          width: 100%;
          max-width: 400px;
          text-align: center;
        }
        .passkey-icon {
          margin-bottom: 24px;
        }
        .passkey-challenge-card h2 {
          font-size: 22px;
          font-weight: 700;
          color: var(--color-title);
          margin: 0 0 12px 0;
        }
        .passkey-challenge-card p {
          font-size: 14px;
          color: var(--color-muted);
          margin: 0 0 24px 0;
          line-height: 1.5;
        }
        .passkey-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid var(--color-border);
          border-top-color: var(--color-brand);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 24px;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .passkey-cancel, .passkey-retry {
          padding: 10px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border: 1px solid var(--color-border);
          background: white;
          color: var(--color-ink);
          transition: background 0.15s;
        }
        .passkey-cancel:hover, .passkey-retry:hover {
          background: var(--color-subsurface);
        }
        .passkey-error-msg {
          color: var(--color-danger) !important;
        }
      `}</style>
    </div>
  );
};
